import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import PlusIcon from "../../../../assets/icons/PlusIcon";
import Spinner from "../../../../assets/icons/Spinner";
import label from "../../../../i18n/label";
import Message from "../../../../i18n/Message";

import ModalDialog from "../../../shared/ModalDialog";

import "./CreateChatDialog.scss";
import backend from "../../../../backend/backend";
import Notify from "../../../shared/Notify";
import ChatActions from "../store/chatActions";
import { useLinkBuilder } from "../../../shared/link-builder/linkBuilderContext";

import ModelSelectionPanel from "./ModelSelectionPanel";
import { linkToNewChat } from "../../../../routes";

const CreateChatDialog = ({ onClose }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const nav = useNavigate();
  const { folderId, workspaceId } = useParams();

  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [modelId, setModelId] = useState(null);
  const [assistantUid, setAssistantUid] = useState(null);
  const [showModelError, setShowModelError] = useState(false);

  const [formElem, setFormElem] = useState(null);
  const [nameInput, setNameInput] = useState(null);

  useEffect(() => {
    if (nameInput) nameInput.focus();
  }, [nameInput]);

  // Hide error when a model or assistant is selected
  useEffect(() => {
    if (modelId || assistantUid) {
      setShowModelError(false);
    }
  }, [modelId, assistantUid]);

  const onCreate = async () => {
    const valid = nameInput.checkValidity();
    if (formElem) formElem.reportValidity();
    if (!valid) return;

    // Check if model or assistant is selected
    if (!modelId && !assistantUid) {
      setShowModelError(true);
      return;
    }

    setLoading(true);

    const onSuccess = () => {
      setLoading(false);
      onClose();
    };

    const payload = {
      name,
      folderId,
      systemPrompt:
        "You are a helpful AI assistant. Follow the user's instructions carefully. Respond using markdown.",
    };

    // Use assistantUid if an assistant is selected, otherwise use modelId
    if (assistantUid) {
      payload.assistantUid = assistantUid;
    } else {
      payload.modelId = modelId;
    }

    backend
      .createChat(workspaceId, payload)
      .then((chat) => {
        console.log("Created chat: ", chat);
        dispatch({
          type: ChatActions.LOADED,
          chat,
        });
        onSuccess();
        nav(linkToNewChat(workspaceId, chat.chat.uid));
      })
      .catch((err) => {
        console.error("Chat creation failed ", err);
        Notify.error(label(intl, "error_create_chat"));
        setLoading(false);
      });
  };
  const onSubmit = (e) => {
    e.preventDefault();
    onCreate();
  };

  return (
    <ModalDialog onClose={onClose} className="create_chat_dialog">
      <div className="modal_title">
        <Message id={"create_chat_title"} />
      </div>
      <form onSubmit={onSubmit} ref={(val) => setFormElem(val)}>
        <div className="dialog_body">
          <input
            className="form-control"
            type="text"
            ref={(val) => setNameInput(val)}
            required
            maxLength="120"
            placeholder={label(intl, "chat_name_placeholder")}
            onChange={(e) => setName(e.target.value)}
            value={name}
            readOnly={loading}
          />
          <ModelSelectionPanel
            modelId={modelId}
            setModelId={setModelId}
            showAssistants={true}
            assistantUid={assistantUid}
            setAssistantUid={setAssistantUid}
          />
        </div>

        {showModelError && (
          <div className="model_error_message">
            <Message id="error_model_required" />
          </div>
        )}

        <div className="buttons">
          <button
            className="btn bg-gradient-dark"
            type="submit"
            disabled={loading}
          >
            {loading ? (
              <Spinner size="18" />
            ) : (
              <PlusIcon color="#fff" strokeWidth="2" size="18" />
            )}
            <Message id={"create_chat_button"} />
          </button>
          <button className="btn no-btn" onClick={onClose}>
            <Message id="cancel" />
          </button>
        </div>
      </form>
    </ModalDialog>
  );
};

export default CreateChatDialog;
